import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { NAATI_FAQ_CATEGORIES, getNaatiCategoryDef, type NaatiCategoryDef } from "@/content/naati-corpus/faq-defs";

export const Route = createFileRoute("/en/naati/faq/$category")({
  loader: async ({ params }): Promise<{ def: NaatiCategoryDef; faqs: { q: string; a: string }[]; keywords: string[] }> => {
    const [{ getNaatiFaqsByCategoryEn }, { NAATI_KEYWORDS_EN_TOP }] = await Promise.all([
      import("@/content/naati-corpus/categories-en"),
      import("@/content/naati-corpus/top.gen"),
    ]);
    const def = getNaatiCategoryDef(params.category);
    if (!def) throw notFound();
    return { def, faqs: getNaatiFaqsByCategoryEn(def.slug), keywords: NAATI_KEYWORDS_EN_TOP.slice(0, 60) };
  },
  component: NaatiFaqCategoryEn,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Category not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${def.titleEn} — NAATI Translation FAQ (${faqs.length} answers, 2026)`,
        description: def.descEn,
        path: `/en/naati/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "th", path: `/naati/faq/${params.category}` }],
        keywords: loaderData.keywords,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "NAATI Translation", url: `${site}/en/naati` },
              { name: "NAATI FAQ", url: `${site}/en/naati/faq` },
              { name: def.titleEn, url: `${site}/en/naati/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/naati/faq/${params.category}`)) },
      ],
    };
  },
});

function NaatiFaqCategoryEn() {
  const { def, faqs } = Route.useLoaderData() as {
    def: NaatiCategoryDef;
    faqs: { q: string; a: string }[];
  };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/naati" className="hover:underline">NAATI Translation</a> <span>/</span>{" "}
          <a href="/en/naati/faq" className="hover:underline">FAQ</a> <span>/</span> <span>{def.titleEn}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{def.titleEn}</h1>
        <p className="text-muted-foreground mb-6">{def.descEn}</p>
        <p className="text-sm text-muted-foreground mb-8">
          <strong>{faqs.length}</strong> practitioner-written answers in this topic — updated for 2026.{" "}
          <a href={`/naati/faq/${def.slug}`} className="text-primary underline">ภาษาไทย →</a>
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-xs">
          {NAATI_FAQ_CATEGORIES.map((c) => (
            <a
              key={c.slug}
              href={`/en/naati/faq/${c.slug}`}
              className={`rounded-full border px-3 py-1 ${c.slug === def.slug ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
            >
              {c.titleEn}
            </a>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((item, i) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Need this document NAATI-certified?</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Send a scan → NAATI certified translation → QA → PDF/courier delivery → lodge with DHA / INZ / IRCC.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">Get a quote →</a>
        </section>

        <FaqSourceNote topic="translation" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
